import React, { useEffect } from "react";
import { Link } from "react-router-dom";

// 공통 컴포넌트
import TextInputBox from "components/elements/TextInputBox";
import PasswordInputBox from "components/elements/PasswordInputBox";
import CheckBox from "components/elements/CheckBox";

// 공통 유틸 함수
import {
  callLoadingEvent,
  getElementCheck,
  getElementValue,
  getParameter
} from "utils/CommonUtils";
import {
  inputAlertHandler,
  inputLimitEventHandler
} from "utils/InputUtils";

// API
import {
  loginApi,
  loginInfoSaveApi,
  tokenLoginApi
} from "api/loginApi";

// 이미지 (SVG)
import { ReactComponent as SiteNameImage } from "assets/images/siteName.svg";
import { ReactComponent as UserIcon } from "assets/images/icon/userIcon.svg";
import { ReactComponent as PasswordIcon } from "assets/images/icon/passwordIcon.svg";

// CSS
import "pages/login/Login.css";
import {WEB_VERSION} from '../../constants/apiUrls'


const Login = () => {
  useEffect(() => {

    if (window.localStorage.getItem("version") !== WEB_VERSION) return;

    /** ✅ 입력 제한 및 경고 처리 이벤트 등록 */
    inputLimitEventHandler();
    inputAlertHandler();

    /** ✅ URL 파라미터 기반 자동 로그인 처리 */
    const getToken = getParameter("token");
    const getUserId = getParameter("userId");
    const getUserType = getParameter("userType");
    const getAdmin = getParameter("admin");

    if (getAdmin) {
      // 관리자 로그인
      loginInfoSaveApi(getUserId, "admin", false)
        .then(() => {})
        .catch(() => {
          alert("해당 상점정보가 없습니다.");
        });
    } else if (getToken) {
      // POS 토큰 로그인
      tokenLoginApi(getUserId, getUserType, getToken)
        .then(() => {})
        .catch(() => {
          alert("일치하는 회원정보가 없습니다.");
        });
    }

    /** ✅ 엔터 키 입력 시 로그인 처리 */
    const passwordInput = document.getElementById("password");
    const handleEnter = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        loginEvent();
      }
    };

    passwordInput.addEventListener("keypress", handleEnter);

    // 언마운트 시 이벤트 제거
    return () => {
      passwordInput.removeEventListener("keypress", handleEnter);
    };
  }, []);

  /** ✅ 로그인 이벤트 */
  const loginEvent = () => {
    const loginId = getElementValue("loginId");
    const password = getElementValue("password");
    const keepCheck = getElementCheck("keepCheck");

    // 아이디 유효성 체크
    if (!loginId) {
      alert("아이디를 입력해주세요.");
      document.getElementById("loginIdAlert").classList.remove("d-none");
      return false;
    }

    // 비밀번호 유효성 체크
    if (!password) {
      alert("비밀번호를 입력해주세요.");
      document.getElementById("passwordAlert").classList.remove("d-none");
      return false;
    }

    // 로딩 팝업 실행
    callLoadingEvent(1);

    // 로그인 API 호출
    loginApi(loginId, password, keepCheck)
      .then(() => {})
      .catch(() => {
        callLoadingEvent(2);
        alert("일치하는 회원정보가 없습니다.");
      });
  };

  return (
    <>
      {/* ✅ 전역 로딩 팝업 */}
      <section id="loadingPopup" className="popup d-none a-center j-center">
        <div className="loadingio-spinner-rolling-10rx3c6i88xj">
          <div className="ldio-5sgltw2ts33">
            <div></div>
          </div>
        </div>
      </section>

      {/* ✅ 로그인 메인 영역 */}
      <main className="wt-full ht-full d-flex a-center j-center">
        <form className="loginBox bg-white br-10">
          {/* 사이트 로고 & 타이틀 */}
          <div className="d-flex a-start j-between mb-25">
            <div>
              <SiteNameImage width={182} />
              <h2 className="fs-28 fw-7">회원 로그인</h2>
            </div>
          </div>

          {/* 아이디 입력 */}
          <div className="loginInputBox wt-full br-lightgray1 d-flex a-center br-10">
            <UserIcon width={24} height={24} className="ml-10" />
            <TextInputBox
              id="loginId"
              max={16}
              className="eng alert br-none pl-10 pr-10 fs-16 fw-7"
              hint="아이디"
            />
          </div>
          <span
            id="loginIdAlert"
            className="fc-red fs-14 fw-5 d-none"
          >
            아이디를 입력해주세요.
          </span>

          {/* 비밀번호 입력 */}
          <div className="loginInputBox wt-full br-lightgray1 d-flex a-center br-10 mt-20">
            <PasswordIcon width={24} height={24} className="ml-10" />
            <PasswordInputBox
              id="password"
              max={16}
              className="alert br-none pl-10 pr-10 fs-16 fw-7"
              hint="비밀번호"
            />
          </div>
          <span
            id="passwordAlert"
            className="fc-red fs-14 fw-5 d-none"
          >
            비밀번호를 입력해주세요.
          </span>

          {/* 자동 로그인 체크 */}
          <div className="mt-20">
            <CheckBox id="keepCheck" checked={false} text="자동 로그인" />
          </div>

          {/* 로그인 버튼 */}
          <button
            id="loginBtr"
            type="button"
            className="wt-full bg-blue fs-20 fw-7 fc-white mt-20"
            onClick={loginEvent}
          >
            로그인
          </button>

          {/* 회원가입 / 아이디찾기 / 비밀번호 변경 */}
          <div className="d-flex a-center j-between mt-15">
            <Link className="fc-lightgray fw-5" to="/v2/application">
              입점신청(회원가입)
            </Link>
            <div className="d-flex a-center j-right">
              <Link className="fw-5 fs-15 fc-lightgray" to="/v2/findId">
                아이디 찾기
              </Link>
              <span className="fs-14 fc-lightgray ml-5 mr-5">|</span>
              <Link className="fw-5 fs-15 fc-lightgray" to="/v2/resetPassword">
                비밀번호 변경
              </Link>
            </div>
          </div>
        </form>
      </main>
    </>
  );
};

export default Login;
